import { useEffect, useRef } from 'react';
import { ExternalLink, Expand } from 'lucide-react';
import useIsMobile from './hooks/useIsMobile';
import useInView from '../hooks/useInView';
import LazyImage from './LazyImage';
import { getTechIconLabel } from '../utils/techIconLabels';
import './WorkContainer.css';

function ProjectMedia({
  useVideo,
  screenshot,
  title,
  logo,
  className,
  logoClassName,
  inView,
  showLogo,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !useVideo) return;

    if (inView) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [inView, useVideo]);

  if (!inView) {
    if (showLogo && logo) {
      return (
        <LazyImage
          className={logoClassName || className}
          src={logo}
          alt={title}
        />
      );
    }
    return <div className={`${className} media-placeholder`} aria-hidden="true" />;
  }

  if (useVideo) {
    return (
      <video
        ref={videoRef}
        className={className}
        src={screenshot}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <LazyImage
      className={className}
      src={screenshot}
      alt={`${title} Screenshot`}
    />
  );
}

function WorkItem({
  title,
  logo,
  screenshot,
  github,
  link,
  icons,
  description,
  fontFamily,
  fontSize,
  useVideo,
  openModal,
  openMediaModal,
  priority = false,
}) {
  const isMobile = useIsMobile();
  const [containerRef, inView] = useInView();
  const shouldLoad = priority || inView;

  return (
    <div className="work-container" ref={containerRef}>
      {!isMobile && (
        <div className="container-left">
          <LazyImage
            className="project-logo"
            src={logo}
            alt={title}
            eager={priority}
          />
          <ProjectMedia
            useVideo={useVideo}
            screenshot={screenshot}
            title={title}
            logo={logo}
            className="project-screenshot"
            inView={shouldLoad}
            showLogo
          />

          <div className="screenshot-overlay">
            <a href={link} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="overlay-icon" />
            </a>
            <span onClick={() => openMediaModal(screenshot, useVideo)}>
              <Expand className="overlay-icon" />
            </span>
          </div>
        </div>
      )}

      <div className="container-right">
        <div
          className={`project-title ${fontSize === 'small' ? 'project-title-small' : ''}`}
          style={{
            fontFamily: `var(--font-${fontFamily.toLowerCase().replace(/\s+/g, '')})`,
          }}
        >
          {title}
          <a href={github} target="_blank" rel="noopener noreferrer">
            <LazyImage className="github" src="/images/github.png" alt="GitHub" />
          </a>
          <a href={link} target="_blank" rel="noopener noreferrer">
            <LazyImage className="link" src="/images/link.png" alt="Link" />
          </a>
        </div>

        {isMobile && (
          <div className="mobile-screenshot-wrapper">
            <ProjectMedia
              useVideo={useVideo}
              screenshot={screenshot}
              title={title}
              logo={logo}
              className="mana-mind-screenshot-mobile"
              logoClassName="mana-mind-screenshot-mobile"
              inView={shouldLoad}
              showLogo
            />
            <LazyImage
              className="mana-mind-logo-mobile"
              src={logo}
              alt={title}
              eager={priority}
            />
            <div className="mobile-screenshot-overlay">
              <a href={link} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="overlay-icon-mobile" />
              </a>
              <span onClick={() => openMediaModal(screenshot, useVideo)}>
                <Expand className="overlay-icon-mobile" />
              </span>
            </div>
          </div>
        )}

        <div className="container-right-bottom">
          <div className="work-content">
            <div className="icon-row">
              {shouldLoad &&
                icons.map((icon) => {
                  const label = getTechIconLabel(icon);

                  return (
                    <div className="icon-tooltip" key={icon}>
                      <LazyImage className="icon" src={icon} alt={label} />
                      <span className="icon-tooltip-label" role="tooltip">
                        {label}
                      </span>
                    </div>
                  );
                })}
            </div>
            <div className="text" onClick={() => openModal(description)}>
              {description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkItem;
